import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi, setTokens, clearTokens, getStoredUser, storeUser, getStoredModules } from '../api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const [modules, setModules] = useState(getStoredModules);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [licenseStatus, setLicenseStatus] = useState('active'); // active, trial, expired
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);

  // Check if stored token is still valid on mount
  useEffect(() => {
    const checkAuth = async () => {
      const stored = getStoredUser();
      if (!stored) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await authApi.me();
        setUser(data.user);
        setModules(data.modules);
        setPermissions(data.permissions || []);
        storeUser(data.user, data.modules);
      } catch {
        clearTokens();
        setUser(null);
        setModules([]);
        setPermissions([]);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Hydrate the context from a login-shaped response. Reused by /register
  // auto-login so a new operator lands straight in the app without a
  // separate /login round-trip.
  const hydrateSession = useCallback((data) => {
    setTokens(data.accessToken, data.refreshToken);
    setUser(data.user);
    setModules(data.modules || []);
    setPermissions(data.permissions || []);
    setLicenseStatus(data.licenseStatus || 'active');
    setTrialDaysLeft(data.trialDaysLeft);
    storeUser(data.user, data.modules || []);
  }, []);

  const login = useCallback(async (email, password, orgSlug, orgId, mfaCode) => {
    const { data } = await authApi.login(email, password, orgSlug, orgId, mfaCode);
    // Multi-org picker — caller continues with selectOrg.
    if (data.pickOrg) return data;
    // MFA challenge — caller continues with completeMfaLogin(mfaChallenge, code).
    if (data.requiresMfa) return data;
    hydrateSession(data);
    return data;
  }, [hydrateSession]);

  const completeMfaLogin = useCallback(async (mfaChallenge, mfaCode) => {
    const { data } = await authApi.loginMfa(mfaChallenge, mfaCode);
    hydrateSession(data);
    return data;
  }, [hydrateSession]);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch {}
    clearTokens();
    setUser(null);
    setModules([]);
    setPermissions([]);
    setLicenseStatus('active');
    setTrialDaysLeft(null);
  }, []);

  const hasRole = useCallback((...roles) => {
    return user && roles.includes(user.role);
  }, [user]);

  const hasModule = useCallback((mod) => {
    return modules.includes(mod);
  }, [modules]);

  const hasPermission = useCallback((permType, disciplineId) => {
    if (user?.role === 'admin' || user?.role === 'super_admin') return true;
    return permissions.some(p =>
      p.permission_type === permType &&
      (disciplineId ? p.discipline_id === disciplineId : true)
    );
  }, [user, permissions]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isStaff = user?.role === 'staff' || isAdmin;
  const isStudent = user?.role === 'student';
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <AuthContext.Provider value={{
      user, modules, permissions, loading,
      login, logout, hydrateSession, completeMfaLogin,
      hasRole, hasModule, hasPermission,
      isAdmin, isStaff, isStudent, isSuperAdmin,
      isAuthenticated: !!user,
      licenseStatus, trialDaysLeft,
      isTrialExpired: licenseStatus === 'expired',
    }}>
      {children}
    </AuthContext.Provider>
  );
};
